import { useElplanBidDayTableSaveMutation } from 'api/elplan/elplan.api'
import type { ElplanBidDayTableBody } from 'api/elplan/elplan.types'
import type { TFunction } from 'i18next'
import { useRef, useState } from 'react'
import alertStore from 'store/alert/alert'
import type { RowValues } from 'ui/components/HourlyDataTable/HourlyDataTable.types'
import Datetime from 'utils/datetime/datetime'
import { buildDayAheadBidDayTableBody } from './DayAheadBidTable.dayTableBody.helper'
import {
  describeDayAheadBidSaveRejection,
  describeDayAheadBidSaveRejectionAlert,
} from './DayAheadBidTable.faultPresentation.helper'
import { findDayAheadBidLocalCellFaults, toDayAheadBidSaveRejection } from './DayAheadBidTable.faults.helper'
import type { DayAheadBidColumn, DayAheadBidRow, DayAheadBidSaveRejection } from './DayAheadBidTable.types'

type UseDayAheadBidTableSaveInput = {
  values: RowValues[]
  configuredColumns: DayAheadBidColumn[]
  renderedColumns: DayAheadBidColumn[]
  rows: DayAheadBidRow[]
  rowLabels: string[]
  elplanInfoId: number
  startTime: ISODateTime
  partitionId: number | undefined
  isReady: boolean
  isDirty: boolean
  saveContextKey: string
  commit: (submittedValues?: RowValues[]) => void
  t: TFunction
}

type DayAheadBidSaveRequestInput = Pick<
  UseDayAheadBidTableSaveInput,
  'values' | 'configuredColumns' | 'rows' | 'elplanInfoId' | 'startTime' | 'partitionId'
>

type DayAheadBidSaveRequest =
  | { kind: 'accepted'; body: ElplanBidDayTableBody }
  | { kind: 'rejected'; rejection: DayAheadBidSaveRejection }

function prepareDayAheadBidSaveRequest({
  values,
  configuredColumns,
  rows,
  elplanInfoId,
  startTime,
  partitionId,
}: DayAheadBidSaveRequestInput): DayAheadBidSaveRequest {
  const localFaults = findDayAheadBidLocalCellFaults({ values, columns: configuredColumns, rows })
  if (localFaults.length > 0) {
    return {
      kind: 'rejected',
      rejection: {
        faults: localFaults,
        rejectedValues: values,
        requestAlert: undefined,
        errorCode: undefined,
      },
    }
  }

  try {
    const body = buildDayAheadBidDayTableBody({
      valuesByRow: values,
      columns: configuredColumns,
      rows,
      elplanInfoId,
      date: Datetime.toLocalTime(startTime, 'onlyDate'),
      partitionId,
    })
    return { kind: 'accepted', body }
  } catch (error) {
    return { kind: 'rejected', rejection: toDayAheadBidSaveRejection(error, values, rows) }
  }
}

export function useDayAheadBidTableSaveBehavior({
  values,
  configuredColumns,
  renderedColumns,
  rows,
  rowLabels,
  elplanInfoId,
  startTime,
  partitionId,
  isReady,
  isDirty,
  saveContextKey,
  commit,
  t,
}: UseDayAheadBidTableSaveInput) {
  const currentSaveContextKeyRef = useRef(saveContextKey)
  currentSaveContextKeyRef.current = saveContextKey
  const currentReadyRef = useRef(isReady)
  currentReadyRef.current = isReady
  const currentValuesRef = useRef(values)
  currentValuesRef.current = values
  const isSaveInFlightRef = useRef(false)
  const [rejection, setRejection] = useState<DayAheadBidSaveRejection | null>(null)
  const [renderedContextKey, setRenderedContextKey] = useState(saveContextKey)
  const saveMutation = useElplanBidDayTableSaveMutation()

  if (renderedContextKey !== saveContextKey) {
    setRenderedContextKey(saveContextKey)
    setRejection(null)
  }
  if (!isDirty && rejection !== null) {
    setRejection(null)
  }

  function handleSave(): void {
    if (!canStartSave()) {
      return
    }

    const submittedValues = values.map((row) => ({ ...row }))
    const request = prepareDayAheadBidSaveRequest({
      values: submittedValues,
      configuredColumns,
      rows,
      elplanInfoId,
      startTime,
      partitionId,
    })
    if (request.kind === 'rejected') {
      recordAndAnnounceRejection(request.rejection)
      return
    }

    setRejection(null)
    submitSave(request.body, submittedValues)
  }

  function canStartSave(): boolean {
    return isReady && !isSaveInFlightRef.current && currentSaveContextKeyRef.current === saveContextKey
  }

  function submitSave(body: ElplanBidDayTableBody, submittedValues: RowValues[]): void {
    isSaveInFlightRef.current = true
    const submittedContextKey = saveContextKey
    const isSubmittedSaveStillApplicable = () =>
      currentSaveContextKeyRef.current === submittedContextKey && currentReadyRef.current
    try {
      saveMutation.mutate(body, {
        onSuccess: () => {
          isSaveInFlightRef.current = false
          if (!isSubmittedSaveStillApplicable()) {
            return
          }
          commit(submittedValues)
          setRejection(null)
          alertStore.success(t('The table was saved.'))
        },
        onError: (error) => {
          isSaveInFlightRef.current = false
          if (!isSubmittedSaveStillApplicable()) {
            return
          }
          rejectWithError(error, submittedValues)
        },
      })
    } catch (error) {
      isSaveInFlightRef.current = false
      rejectWithError(error, submittedValues)
    }
  }

  function rejectWithError(error: unknown, rejectedValues: RowValues[]): void {
    recordAndAnnounceRejection(toDayAheadBidSaveRejection(error, rejectedValues, rows))
  }

  const faultDisplayContext = { rows, rowLabels, configuredColumns, renderedColumns, translate: t }

  function recordAndAnnounceRejection(nextRejection: DayAheadBidSaveRejection): void {
    setRejection(nextRejection)
    alertStore.error(
      describeDayAheadBidSaveRejectionAlert(nextRejection, {
        ...faultDisplayContext,
        values: currentValuesRef.current,
      }),
      undefined,
      nextRejection.errorCode
    )
  }

  const { faultMessageByCellKey, bannerMessage } = describeDayAheadBidSaveRejection(rejection, {
    ...faultDisplayContext,
    values,
  })

  return {
    handleSave,
    isPending: saveMutation.isPending,
    faultMessageByCellKey,
    bannerMessage,
  }
}
